import {
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { Download, Edit, Eye, Receipt, Trash2 } from "lucide-react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { openDownloadLinkCallback } from "utils/download";
import { ClipConfiguration, Status } from "../clip-ui/models";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { PermissionTooltipWrapper } from "components/permissions/permissions";

interface RowActionsMenuProps {
  anchorEl: HTMLElement | null;
  selectedClip: ClipConfiguration | null;
  nonEditableStatuses: Status[];
  onClose: () => void;
  onDelete: () => void;
}

/**
 * Context menu for clip row actions (View, Download, Edit, Invoice, Delete)
 */
export const RowActionsMenu = ({
  anchorEl,
  selectedClip,
  nonEditableStatuses,
  onClose,
  onDelete,
}: RowActionsMenuProps) => {
  const history = useHistory();
  const isCompleted = selectedClip?.status === "Completed";

  // Check if clip has premium data to determine which permission to check
  const clipHasPremiumData = selectedClip ? hasPremiumData(selectedClip) : false;

  // Check if clip can be deleted (not Processing or Quoting)
  const canDeleteClip =
    selectedClip?.status !== "Processing" && selectedClip?.status !== "Quoting";

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      <MenuItem
        onClick={() => {
          if (selectedClip) {
            history.push(`${Routes.clipUi}?clip_id=${selectedClip.id}`);
          }
          onClose();
        }}
      >
        <ListItemIcon>
          {isCompleted ? <Eye size={18} /> : <Edit size={18} />}
        </ListItemIcon>
        <ListItemText>{isCompleted ? "View" : "Edit"}</ListItemText>
      </MenuItem>

      {clipHasPremiumData ? (
        <PermissionTooltipWrapper
          permissionKeys={[PermissionsKeys.premiumDataRead]}
          isDisabled={selectedClip?.status !== "Completed"}
        >
          <MenuItem
            onClick={() => {
              if (selectedClip) {
                openDownloadLinkCallback(
                  `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${selectedClip.id}/output`
                )();
              }
              onClose();
            }}
          >
            <ListItemIcon>
              <Download size={18} />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        </PermissionTooltipWrapper>
      ) : (
        <MenuItem
          onClick={() => {
            if (selectedClip) {
              openDownloadLinkCallback(
                `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${selectedClip.id}/output`
              )();
            }
            onClose();
          }}
          disabled={selectedClip?.status !== "Completed"}
        >
          <ListItemIcon>
            <Download size={18} />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
      )}

      <MenuItem
        onClick={() => {
          if (selectedClip?.invoiceUrl) {
            window.open(selectedClip.invoiceUrl, "_blank");
          }
          onClose();
        }}
        disabled={!selectedClip?.invoiceId}
      >
        <ListItemIcon>
          <Receipt size={18} />
        </ListItemIcon>
        <ListItemText>Invoice</ListItemText>
      </MenuItem>

      <Divider />

      <PermissionTooltipWrapper
        permissionKeys={[PermissionsKeys.clipDelete]}
        isDisabled={!canDeleteClip}
      >
        <MenuItem
          onClick={() => {
            onDelete();
            onClose();
          }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <Trash2 size={18} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { color: 'error.main' } }}>
            Delete
          </ListItemText>
        </MenuItem>
      </PermissionTooltipWrapper>
    </Menu>
  );
};

interface ColumnVisibilityMenuProps {
  anchorEl: HTMLElement | null;
  columnVisibility: Record<string, boolean>;
  onClose: () => void;
  onToggle: (column: string) => void;
}

/**
 * Menu for toggling column visibility
 */
export const ColumnVisibilityMenu = ({
  anchorEl,
  columnVisibility,
  onClose,
  onToggle,
}: ColumnVisibilityMenuProps) => {
  const columns = [
    { key: "name", label: "Name" },
    { key: "clientReference", label: "Client Ref" },
    { key: "totalCredits", label: "Credits" },
    { key: "totalDollars", label: "Cost ($)" },
    { key: "status", label: "Status" },
    { key: "premium", label: "Premium" },
    { key: "author", label: "Author" },
    { key: "lastEditedAt", label: "Modified" },
    { key: "purchasedOn", label: "Purchased" },
    { key: "completedOn", label: "Completed" },
    { key: "actions", label: "Actions" },
  ];

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {columns.map(({ key, label }) => (
        <MenuItem
          key={key}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(key);
          }}
        >
          <Checkbox checked={columnVisibility[key]} />
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );
};
